/**
 * WebView ↔ H5（WebViewJavascriptBridge）。
 *
 * 策略：
 * - 启动/回前台暖机，尽早注入桥
 * - 调用前先判断桥是否存在；存在才 callHandler，不存在绝不盲调
 * - 非浏览器可短等注入；仍无桥 → 返回 false，由调用方 H5 保底
 *
 * ## openPantaSafariURL
 * data：url（必填）、name（可选）
 */

export type PantaSafariPayload = {
  url: string;
  name?: string;
};

export type RuntimeEnvKind = 'browser' | 'app' | 'webview' | 'dapp';

type WVJBBridge = {
  callHandler: (handlerName: string, data?: unknown, responseCallback?: (res: unknown) => void) => void;
};

/** 等待桥就绪的最长时间（App 冷启动注入常晚于首屏） */
const BRIDGE_WAIT_MS = 2500;

const getUa = (): string =>
  typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';

/** 是否打开详细桥日志（宿主 App / 已有桥 / 显式 debug） */
const bridgeVerbose = (): boolean => {
  try {
    if (String(import.meta.env.VITE_ENABLE_DEBUG_LOG || '') === 'true') return true;
  } catch {
    /* ignore */
  }
  if (typeof window === 'undefined') return false;
  if (hasHostAppMark()) return true;
  return !!peekBridge();
};

/**
 * [PantaBridge] 在 index.html 白名单不过滤。
 * 普通浏览器 H5：日常 probe 静默；FAIL 仍打出。宿主 App / 有桥 / debug 才打全量。
 */
const bridgeLog = (step: string, detail?: unknown): void => {
  try {
    const isFail = String(step).indexOf('FAIL') !== -1;
    if (!isFail && !bridgeVerbose()) return;
    if (detail !== undefined) {
      console.warn(`[PantaBridge] ${step}`, detail);
    } else {
      console.warn(`[PantaBridge] ${step}`);
    }
  } catch {
    /* ignore */
  }
};

const bridgeLogFail = (step: string, err?: unknown, extra?: Record<string, unknown>): void => {
  const e = err as { message?: string; stack?: string; name?: string } | undefined;
  bridgeLog(`FAIL: ${step}`, {
    errorMessage: e?.message || String(err ?? ''),
    errorName: e?.name,
    errorStack: e?.stack,
    ...extra,
    env: getDeviceEnvInfo(),
  });
};

const hasHostAppMark = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (/AstralApp|AstralX|Astralx/i.test(getUa())) return true;
  const w = window as unknown as {
    AstralX?: unknown;
    astralx?: unknown;
    __ASTRALX__?: unknown;
    astralX?: unknown;
  };
  return !!(w.AstralX || w.astralx || w.__ASTRALX__ || w.astralX);
};

/**
 * Chrome DevTools「手机模式」会伪装 iPhone UA，但底层仍是桌面 Chrome。
 * 此时不应走 WVJB iframe（会报 wvjbscheme 无 handler）。
 */
const isDesktopDevtoolsMobileSpoof = (): boolean => {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  if (hasHostAppMark()) return false;
  const ua = getUa();
  if (!/iPhone|iPad|iPod|Android/i.test(ua)) return false;
  const platform = String(navigator.platform || '');
  // 桌面系统 + 移动 UA ≈ 设备模拟；真机 iPadOS 也会报 MacIntel，但 maxTouchPoints > 1
  if (/MacIntel|Win32|Linux/i.test(platform) && (navigator.maxTouchPoints || 0) <= 1) {
    return true;
  }
  // 桌面 Chrome 扩展运行时（真机 Safari/WKWebView 无此对象）
  const chrome = (window as unknown as { chrome?: { runtime?: unknown } }).chrome;
  if (chrome && typeof chrome.runtime !== 'undefined') return true;
  return false;
};

const isNormalBrowser = (): boolean => {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return true;
  if (isDesktopDevtoolsMobileSpoof()) return true;
  const ua = getUa();
  if (!/Mobile|Android|iPhone|iPad|iPod/i.test(ua)) {
    if (/Chrome|Firefox|Safari|Edg|Opera/i.test(ua)) return true;
  }
  if (/CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo|YaBrowser/i.test(ua)) return true;
  const safari = (window as unknown as { safari?: { pushNotification?: unknown } }).safari;
  if (safari && typeof safari.pushNotification !== 'undefined') return true;
  return false;
};

const isWalletDappBrowser = (): boolean =>
  /MetaMask|TokenPocket|imToken|BitKeep|Bitget|Trust\/|OKApp|OKEx|OKX|Coinbase|Phantom|Rainbow|Rabby|MathWallet|SafePal|Zerion|Frontier|Binance/i.test(
    getUa()
  );

export const detectRuntimeEnv = (): RuntimeEnvKind => {
  if (isWalletDappBrowser()) return 'dapp';
  if (hasHostAppMark() || !!peekBridge()) return 'app';
  if (isNormalBrowser()) return 'browser';
  if (/iPhone|iPad|iPod|Android|;\s*wv\)/i.test(getUa())) return 'webview';
  return 'browser';
};

export type DeviceEnvInfo = {
  env: RuntimeEnvKind;
  platform: 'ios' | 'android' | 'other';
  hasBridge: boolean;
  hostAppMark: boolean;
  normalBrowser: boolean;
  dappBrowser: boolean;
  ua: string;
  language: string;
  standalone: boolean;
};

const peekBridge = (): WVJBBridge | null => {
  if (typeof window === 'undefined') return null;
  const bridge = (window as unknown as { WebViewJavascriptBridge?: WVJBBridge }).WebViewJavascriptBridge;
  return bridge && typeof bridge.callHandler === 'function' ? bridge : null;
};

/** 仅日志用 */
export const getDeviceEnvInfo = (): DeviceEnvInfo => {
  const ua = getUa();
  const platform = /iPhone|iPad|iPod/i.test(ua) ? 'ios' : /Android/i.test(ua) ? 'android' : 'other';
  const standalone =
    typeof window !== 'undefined' &&
    !!(
      (window.navigator as unknown as { standalone?: boolean }).standalone ||
      window.matchMedia?.('(display-mode: standalone)')?.matches
    );
  return {
    env: detectRuntimeEnv(),
    platform,
    hasBridge: !!peekBridge(),
    hostAppMark: hasHostAppMark(),
    normalBrowser: isNormalBrowser(),
    dappBrowser: isWalletDappBrowser(),
    ua,
    language: typeof navigator !== 'undefined' ? navigator.language || '' : '',
    standalone,
  };
};

/** 对齐 App：无桥时 iframe 握手 + 入队（仅宿主 App；桌面/模拟器禁止打 wvjbscheme） */
const setupWebViewJavascriptBridge = (callback: (bridge: WVJBBridge) => void): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const w = window as unknown as {
    WebViewJavascriptBridge?: WVJBBridge;
    WVJBCallbacks?: Array<(bridge: WVJBBridge) => void>;
  };
  if (w.WebViewJavascriptBridge) {
    callback(w.WebViewJavascriptBridge);
    return;
  }
  // 无宿主标记时不要注入 scheme iframe，避免浏览器报 Failed to launch wvjbscheme
  if (!hasHostAppMark()) {
    return;
  }
  if (w.WVJBCallbacks) {
    w.WVJBCallbacks.push(callback);
    return;
  }
  w.WVJBCallbacks = [callback];
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = 'wvjbscheme://__BRIDGE_LOADED__';
  document.documentElement.appendChild(iframe);
  setTimeout(() => {
    try {
      document.documentElement.removeChild(iframe);
    } catch {
      /* ignore */
    }
  }, 0);
};

/** 提前握手暖机（不调业务 handler；仅 AstralX 等宿主） */
const warmBridge = (reason: string): void => {
  if (!hasHostAppMark() && !peekBridge()) return;
  try {
    setupWebViewJavascriptBridge((bridge) => {
      bridgeLog(`bridge ready (${reason})`, getDeviceEnvInfo());
      void bridge;
    });
  } catch (err) {
    bridgeLogFail(`warmBridge failed (${reason})`, err);
  }
};

/**
 * 等待桥就绪：WVJB 回调 + 轮询双通道（应对 App 晚注入）。
 */
const waitForBridge = (timeoutMs: number): Promise<WVJBBridge | null> =>
  new Promise((resolve) => {
    const existing = peekBridge();
    if (existing) {
      resolve(existing);
      return;
    }

    let settled = false;
    const finish = (bridge: WVJBBridge | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      clearInterval(poll);
      resolve(bridge);
    };

    const timer = setTimeout(() => {
      bridgeLogFail('waitForBridge timeout', undefined, { timeoutMs });
      finish(peekBridge());
    }, timeoutMs);

    const poll = setInterval(() => {
      const b = peekBridge();
      if (b) {
        bridgeLog('waitForBridge got bridge (poll)', getDeviceEnvInfo());
        finish(b);
      }
    }, 100);

    try {
      setupWebViewJavascriptBridge((bridge) => {
        bridgeLog('waitForBridge got bridge (inject cb)', getDeviceEnvInfo());
        finish(bridge);
      });
    } catch (err) {
      bridgeLogFail('waitForBridge setup threw', err);
    }
  });

const invokeCallHandler = (bridge: WVJBBridge, name: string, data?: unknown): void => {
  bridge.callHandler(name, data, (res) => {
    bridgeLog(`native response ${name}`, res);
  });
};

/**
 * 有桥才 callHandler；无桥不盲调。
 * @returns true = 已发出 callHandler；false → 调用方 H5 保底
 */
const callNativeHandlerAsync = async (name: string, data?: unknown): Promise<boolean> => {
  let bridge = peekBridge();

  // 首屏可能稍晚注入：非浏览器短等；等到仍没有 → 不调用
  if (!bridge && !isNormalBrowser()) {
    bridgeLog('bridge missing, short wait before decide…', {
      name,
      waitMs: BRIDGE_WAIT_MS,
      ...getDeviceEnvInfo(),
    });
    bridge = await waitForBridge(BRIDGE_WAIT_MS);
  }

  if (!bridge) {
    bridgeLog('skip callHandler: bridge not present → H5', {
      name,
      data,
      ...getDeviceEnvInfo(),
    });
    return false;
  }

  try {
    bridgeLog(`callHandler(${name}) (bridge present)`, data);
    invokeCallHandler(bridge, name, data);
    bridgeLog(`callHandler(${name}) ok`);
    return true;
  } catch (err) {
    bridgeLogFail('callHandler threw', err, { name, data });
    return false;
  }
};

const isSafeOpenUrl = (url: string): boolean =>
  /^(https:\/\/|[a-z][a-z0-9+.-]*:\/\/)/i.test(url) && !/^(javascript|data|file|http):/i.test(url);

/**
 * 打开外部钱包：桥存在才 callHandler；否则 false → H5 保底
 */
export const openPantaSafariURL = async (payload: PantaSafariPayload): Promise<boolean> => {
  bridgeLog('openPantaSafariURL', getDeviceEnvInfo());

  if (typeof window === 'undefined') {
    bridgeLogFail('openPantaSafariURL skip: no window');
    return false;
  }

  const url = typeof payload?.url === 'string' ? payload.url.trim() : '';
  if (!url || !isSafeOpenUrl(url)) {
    bridgeLogFail('openPantaSafariURL skip: invalid url', undefined, {
      url: payload?.url,
      name: payload?.name,
    });
    return false;
  }

  const data = {
    url,
    ...(payload.name ? { name: payload.name } : {}),
  };
  const ok = await callNativeHandlerAsync('openPantaSafariURL', data);
  if (!ok) {
    bridgeLog('openPantaSafariURL skipped/failed → caller should H5 fallback', { data });
  }
  return ok;
};

/** 当前是否已有可用桥（供外部快速判断；调用前仍以 openPantaSafariURL 内判定为准） */
export const shouldOpenExternalUrlViaIosBridge = (): boolean => !!peekBridge();

/** 导出探测，便于业务侧「有桥才走桥」 */
export { peekBridge as hasNativeBridge };

let loadProbeStarted = false;
export const logDeviceAndBridgeOnLoad = (): void => {
  if (typeof window === 'undefined' || loadProbeStarted) return;
  loadProbeStarted = true;

  const dump = (when: string) => {
    bridgeLog(`load probe (${when})`, {
      ...getDeviceEnvInfo(),
      policy: 'call only if bridge present, else H5',
    });
  };

  // 仅宿主 App 暖机 + 多次探测；普通浏览器 / DevTools 模拟只打一条，避免刷屏与 wvjbscheme 报错
  const inHostApp = hasHostAppMark();
  warmBridge('boot');
  dump('immediate');
  if (!inHostApp) return;

  setTimeout(() => dump('t+500ms'), 500);
  setTimeout(() => dump('t+2s'), 2000);
  setTimeout(() => dump('t+5s'), 5000);

  const onResume = (reason: string) => {
    dump(reason);
    warmBridge(reason);
  };

  window.addEventListener('pageshow', () => onResume('pageshow'));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') onResume('visibility:visible');
  });
};
